/**
 * Pricing Page - WorkStream
 * Detalhamento de planos e preços
 */
import { Check, X, HelpCircle } from 'lucide-react'
import SEO, { breadcrumbSchema, productSchema } from '../components/SEO'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import CTA from '../components/CTA'

export default function Pricing() {
  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: 'https://workstream.com.br' },
    { name: 'Preços', url: 'https://workstream.com.br/pricing' },
  ])

  const starterProduct = productSchema({
    name: 'WorkStream Starter',
    description: 'Plano para pequenas equipes começarem com monitoramento inteligente',
    price: 10,
    currency: 'BRL',
    availability: 'InStock',
  })

  const plans = [
    {
      name: 'Starter',
      price: 'R$ 10',
      unit: '/colaborador/mês',
      description: 'Para pequenas equipes',
      cta: 'Comece Grátis',
      highlighted: false,
      features: [
        { name: 'Ponto Facial básico', included: true },
        { name: 'Até 50 colaboradores', included: true },
        { name: 'Relatórios mensais', included: true },
        { name: 'Suporte por email', included: true },
        { name: 'Análise de Produtividade', included: false },
        { name: 'IA Comportamental', included: false },
        { name: 'Verificação de EPIs', included: false },
        { name: 'API', included: false },
        { name: 'SSO', included: false },
        { name: 'Suporte prioritário', included: false },
      ],
    },
    {
      name: 'Business',
      price: 'R$ 25',
      unit: '/colaborador/mês',
      description: 'Mais popular',
      cta: 'Começar Trial',
      highlighted: true,
      features: [
        { name: 'Todos os 4 serviços', included: true },
        { name: 'Até 500 colaboradores', included: true },
        { name: 'Relatórios em tempo real', included: true },
        { name: 'Suporte prioritário', included: true },
        { name: 'IA Comportamental', included: true },
        { name: 'Verificação de EPIs', included: true },
        { name: 'API completa', included: true },
        { name: 'Webhooks', included: true },
        { name: 'SSO', included: false },
        { name: 'Gerente dedicado', included: false },
      ],
    },
    {
      name: 'Enterprise',
      price: 'Sob consulta',
      unit: '',
      description: 'Para grandes operações',
      cta: 'Falar com Vendas',
      highlighted: false,
      features: [
        { name: 'Tudo do Business', included: true },
        { name: 'Colaboradores ilimitados', included: true },
        { name: 'ML personalizado', included: true },
        { name: 'On-premise disponível', included: true },
        { name: 'SLA 99.99%', included: true },
        { name: 'Suporte 24/7', included: true },
        { name: 'SSO + SAML 2.0', included: true },
        { name: 'Gerente dedicado', included: true },
        { name: 'Treinamento in-loco', included: true },
        { name: 'Consultoria IA', included: true },
      ],
    },
  ]

  const faqs = [
    {
      q: 'Posso mudar de plano a qualquer momento?',
      a: 'Sim! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. As mudanças são proporcionais ao período restante.',
    },
    {
      q: 'Vocês oferecem período de teste gratuito?',
      a: 'Sim, oferecemos 14 dias de teste gratuito para todos os planos, com acesso completo às funcionalidades. Sem cartão de crédito.',
    },
    {
      q: 'Como funciona a cobrança por colaborador?',
      a: 'Você paga apenas pelos colaboradores ativos no mês. Se um colaborador for desativado, ele deixa de ser cobrado no próximo ciclo.',
    },
    {
      q: 'Vocês têm desconto para pagamento anual?',
      a: 'Sim! Pagamentos anuais ganham 2 meses grátis (equivalente a ~17% de desconto).',
    },
    {
      q: 'Qual plano é melhor para começar?',
      a: 'Recomendamos o Business para a maioria das empresas, pois inclui todos os serviços e suporte prioritário com o melhor custo-benefício.',
    },
    {
      q: 'Posso integrar com meus sistemas existentes?',
      a: 'Sim! Os planos Business e Enterprise têm acesso completo à nossa API REST e webhooks para integrações.',
    },
  ]

  return (
    <>
      <SEO
        title="Preços e Planos"
        description="Planos transparentes a partir de R$ 10/colaborador/mês. Escolha entre Starter, Business ou Enterprise. Teste grátis por 14 dias."
        keywords={[
          'preços workstream',
          'planos ponto facial',
          'quanto custa',
          'trial gratuito',
          'software produtividade preço',
        ]}
        url="https://workstream.com.br/pricing"
        structuredData={[breadcrumbs, starterProduct]}
      />

      <div className="min-h-screen bg-[#0B1120]">
        <Navbar />

        {/* Header */}
        <header className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="text-cyan-400 font-mono text-sm tracking-wider">PLANOS</span>
            <h1 className="text-5xl font-bold text-white mt-4 mb-6">
              Preços Transparentes
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Escolha o plano que melhor se adequa ao tamanho da sua equipe
            </p>
          </div>
        </header>

        {/* Pricing Cards */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl overflow-hidden transition-transform ${
                  plan.highlighted
                    ? 'bg-gradient-to-b from-cyan-500/10 to-transparent border-2 border-cyan-500/30 transform scale-105 shadow-xl shadow-cyan-500/10'
                    : 'bg-slate-800/30 border border-slate-700/50'
                }`}
              >
                {/* Header */}
                <div className="p-8 text-center border-b border-slate-700/50">
                {plan.highlighted && (
                  <div className="mb-4">
                      <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-semibold">
                        MAIS POPULAR
                    </span>
                  </div>
                )}
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-slate-500 text-sm mb-4">{plan.description}</p>
                <div>
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-slate-500 text-sm">{plan.unit}</span>
                  </div>
              </div>

                {/* CTA */}
                <div className="p-6">
                <button
                    className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    plan.highlighted
                        ? 'text-white hover:-translate-y-0.5'
                        : 'bg-slate-700 text-white hover:bg-slate-600'
                  }`}
                    style={plan.highlighted ? { 
                      background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
                      boxShadow: '0 4px 14px 0 rgba(0, 198, 255, 0.39)'
                    } : {}}
                >
                  {plan.cta}
                </button>
              </div>

                {/* Features */}
                <div className="p-6 pt-0">
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature.name} className="flex items-start gap-3">
                      {feature.included ? (
                          <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={feature.included ? 'text-slate-300' : 'text-slate-600'}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        </section>

        {/* Comparison Table */}
        <section className="bg-[#0F172A] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-cyan-400 font-mono text-sm tracking-wider">COMPARAÇÃO</span>
              <h2 className="text-3xl font-bold text-white mt-4">Comparação Completa</h2>
            </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                  <tr className="border-b border-slate-700">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-400">Recursos</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-white">Starter</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-cyan-400">Business</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-white">Enterprise</th>
                </tr>
              </thead>
                <tbody className="divide-y divide-slate-800">
                  {[
                    { name: 'Colaboradores', starter: '50', business: '500', enterprise: 'Ilimitado' },
                    { name: 'Ponto Facial', starter: '✅', business: '✅', enterprise: '✅' },
                    { name: 'Produtividade', starter: '❌', business: '✅', enterprise: '✅' },
                    { name: 'IA Comportamental', starter: '❌', business: '✅', enterprise: '✅' },
                    { name: 'Verificação EPIs', starter: '❌', business: '✅', enterprise: '✅' },
                    { name: 'API', starter: '❌', business: '✅', enterprise: '✅' },
                    { name: 'SSO/SAML', starter: '❌', business: '❌', enterprise: '✅' },
                    { name: 'Suporte', starter: 'Email', business: 'Prioritário', enterprise: '24/7' },
                    { name: 'SLA', starter: '99%', business: '99.9%', enterprise: '99.99%' },
                  ].map((row) => (
                    <tr key={row.name} className="hover:bg-slate-800/30">
                      <td className="px-6 py-4 text-sm font-medium text-white">{row.name}</td>
                      <td className="px-6 py-4 text-center text-sm text-slate-400">{row.starter}</td>
                      <td className="px-6 py-4 text-center text-sm text-cyan-400 font-medium">{row.business}</td>
                      <td className="px-6 py-4 text-center text-sm text-slate-400">{row.enterprise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </section>

        {/* FAQ */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-cyan-400 font-mono text-sm tracking-wider">FAQ</span>
              <h2 className="text-3xl font-bold text-white mt-4">Perguntas Frequentes</h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.q} className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/50">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                      <p className="text-slate-400 text-sm">{faq.a}</p>
                    </div>
                  </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTA
        title="Pronto para começar?"
          description="Teste gratuitamente por 14 dias. Sem cartão de crédito."
      />

      <Footer />
    </div>
    </>
  )
}
